/** REȚETELE PROIECTULUI: pagina unică de rețete și resurse (materiale,
 *  manoperă, utilaje, transport).
 *
 *  CE SE VEDE E AL PROIECTULUI. Rețetele și resursele pe care lista de
 *  cantități le folosește se clonează pe proiect la calculul ei
 *  (`control-plane/api/boq.py::_de_clonat`), ca omul să le editeze acolo —
 *  un consum, un preț, o cantitate pusă de mână, o rețetă ascunsă sau una
 *  nouă. Catalogul global rămâne în platformă: din el se mai pot ADĂUGA
 *  rețete și resurse nefolosite încă, iar «Revino la valorile globale»
 *  întoarce o copie modificată la valorile lui. O copie care s-a îndepărtat
 *  de global e marcată «modificată». Se salvează pe server cu garda `rev`
 *  (doi oameni pe același catalog nu se calcă unul pe altul fără să știe).
 *
 *  Prețul unitar al rețetei se recalculează pe loc din resursele proiectului,
 *  ca omul să vadă ce face un consum sau un preț schimbat înainte să salveze.
 *
 *  TOATE REȚETELE ÎNTR-O SINGURĂ PAGINĂ: cele de execuție ale structurii
 *  (fundație, placă pe sol, stâlp, grindă, perete, planșeu — normele pe care
 *  stă devizul structurii, cu tarifele din «Prețuri, zonă & norme») intră în
 *  subiectul «structura», în capitolul lor (infrastructură / suprastructură),
 *  deasupra rețetelor listei din același capitol: în deviz sunt un singur
 *  capitol, deci și aici. Subiectele și căutarea le cern pe toate.
 *
 *  PE SCENARIU. Cu `scenariu`, pagina poate edita catalogul SCENARIULUI
 *  curent în locul celui al proiectului: ecranul arată proiectul cu
 *  suprascrierile scenariului peste (`suprapune`), iar salvarea ține pe
 *  scenariu numai ce diferă de proiect (`suprascrierile`). Ce a schimbat
 *  scenariul e marcat «în scenariu».
 *
 *  PIAȚA. Pe ecranul resurselor, o resursă pe care o urmărește colectorul de
 *  prețuri are butonul «Piața» (`ExploreazaPiata`), iar deasupra tabelului
 *  «Importă prețurile pieței» (`ImportPiata`). Amândouă scriu pe server în
 *  copia PROIECTULUI, deci cât pagina are schimbări nesalvate stau blocate:
 *  după import, salvarea ei ar fi refuzată (garda `rev`) și omul și-ar pierde
 *  editarea. */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError } from "../../api";
import SeIncarca from "../../components/SeIncarca";
import { esteCheie, numar, t, useT, type Cheie } from "../../i18n";
import { bani, pretUnitar } from "../../structure/bani";
import {
  COPII_GOALE, LISTE_RESURSE, TIP_LA_LISTA, cloneazaResursa, cloneazaReteta, cuAscunsa, cuCantitate, cuPretDeOm, cuResursa,
  cuReteta, getCatalogBoq, nrSuprascrieri, platitAltundeva, pretUnitarReteta, putCatalogBoq, resursaDifera, resursaProiectului,
  retetaDifera, retetaProiectului, suprapune, suprascrierile, fazeleResurselor, grupatePeFaze, numeFaza, type Suprascrieri,
  type CatalogGlobal, type Consum, type CopiiProiect, type ListaCantitati, type ListaResurse, type Reteta,
  type TipResursa,
} from "./boq";
import ExploreazaPiata from "./ExploreazaPiata";
import ImportPiata from "./ImportPiata";
import { cheiaResursei, getPiataResurse, type PiataProiect, type Tinta } from "./piataResurse";
import "./piataResurse.css";

const TIPURI: TipResursa[] = ["material", "manopera", "utilaj", "transport"];
const nr = (v: number | null | undefined, z = 3) => (v == null ? "—" : numar(v, { maximumFractionDigits: z }));
const UM: Record<string, string> = { m2: "m²", m3: "m³" };
const um = (u?: string) => (u ? UM[u] ?? u : "");
const numeSubiect = (glob: CatalogGlobal, cod: string) => {
  const k = `lc.subiect.${cod}`;
  return esteCheie(k) ? t(k) : glob.subiecte.find((s) => s.cod === cod)?.nume ?? cod;
};
const numeCapitol = (glob: CatalogGlobal, s: string, cod: string) => {
  const k = `lc.capitol.${s}.${cod}`;
  return esteCheie(k) ? t(k) : glob.capitole[s]?.find((c) => c.cod === cod)?.nume ?? cod;
};
/** Eticheta unei rețete de REZERVĂ (`catalog.INLOCUITE_DE`): rețeta e fixă, ca
 *  oricare; ce se estimează e cantitatea ei, cât lipsește sursa detaliată. */
function Rezerva({ r }: { r: Pick<Reteta, "inlocuit_de"> }) {
  return r.inlocuit_de
    ? <span className="lc-fel lc-fel-global" title={t("lc.catalog.rezerva_t", { sursa: r.inlocuit_de })}>{t("lc.catalog.rezerva")}</span>
    : null;
}
const STRUCTURA = "structura";
/** Capitolul listei al unei rețete de execuție: categoria ei din
 *  `cost_model/retete.py` începe cu «Infrastructură» sau «Suprastructură». */
const capitolExecutie = (r: RetetaStructura) =>
  /^infra/i.test(r.categorie) ? "infra" : /^supra/i.test(r.categorie) ? "supra" : "conexe";

/** O rețetă de execuție a structurii, cât arată pagina de aici: restul îl
 *  desenează părintele (`detaliu`), care are cartea ei de norme. */
export type RetetaStructura = { id: string; nume: string; categorie: string; activitati: { cod: string; nume: string }[] };

/** Ce a schimbat scenariul față de proiect (`null` = se editează proiectul). */
type DiferaDeProiect = ((lista: ListaResurse | "retete", cod: string) => boolean) | null;

function MarcajScenariu({ difera, lista, cod }: { difera: DiferaDeProiect; lista: ListaResurse | "retete"; cod: string }) {
  return difera?.(lista, cod) ? <span className="lc-fel lc-fel-sc">{t("lc.sc.in_scenariu")}</span> : null;
}

function EditorReteta({ glob, copii, cod, potScrie, setCopii, lista, difera = null }: {
  glob: CatalogGlobal; copii: CopiiProiect; cod: string; potScrie: boolean;
  setCopii: (f: (c: CopiiProiect) => CopiiProiect) => void; lista: ListaCantitati | null;
  difera?: DiferaDeProiect;
}) {
  useT();
  const r = retetaProiectului(glob, copii, cod);
  const [tipNou, setTipNou] = useState<TipResursa>("material");
  const [codNou, setCodNou] = useState("");
  if (!r) return null;
  const pu = pretUnitarReteta(glob, copii, r);
  const eGlobala = glob.retete.some((x) => x.cod === cod);
  const modificata = retetaDifera(glob, copii.retete[cod] ?? {}, cod);
  const ascunsa = copii.ascunse.includes(cod);
  const cantitateOm = copii.cantitati[cod];
  const articol = lista?.subiecte.flatMap((s) => s.capitole).flatMap((c) => c.articole).find((a) => a.reteta === cod) ?? null;
  const schimba = (patch: Partial<Reteta>) => setCopii((c) => cuReteta(c, cod, { ...retetaProiectului(glob, c, cod)!, ...patch, din_proiect: undefined }));
  const consum = (i: number, patch: Partial<Consum> | null) => {
    const cons = [...r.consumuri];
    if (patch === null) cons.splice(i, 1); else cons[i] = { ...cons[i], ...patch };
    schimba({ consumuri: cons });
  };
  const adauga = () => {
    if (!codNou) return;
    // o resursă din global care nu e încă a proiectului se clonează odată cu consumul
    setCopii((c) => {
      const l = TIP_LA_LISTA[tipNou];
      const cu = c.resurse[l]?.[codNou] ? c : cuResursa(c, l, codNou, cloneazaResursa(glob, l, codNou) ?? { nume: codNou });
      return cuReteta(cu, cod, { ...retetaProiectului(glob, cu, cod)!, consumuri: [...r.consumuri, { tip: tipNou, cod: codNou, cantitate: 1 }], din_proiect: undefined });
    });
    setCodNou("");
  };
  const lNou = TIP_LA_LISTA[tipNou];
  const optiuni = [...new Set([...Object.keys(glob.resurse[lNou] ?? {}), ...Object.keys(copii.resurse[lNou] ?? {})])]
    .map((k) => resursaProiectului(glob, copii, lNou, k)!)
    .sort((a, b) => (a.nume ?? "").localeCompare(b.nume ?? ""));
  const ro = !potScrie;
  return (
    <div className="lc-editor">
      <div className="lc-editor-h">
        <span className="sg-mono">{r.cod}</span>
        {modificata ? <span className="lc-fel lc-fel-om">{t("lc.catalog.modificata")}</span>
          : <span className="lc-fel lc-fel-global">{t("lc.catalog.ca_global")}</span>}
        <Rezerva r={r} />
        {ascunsa && <span className="lc-fel lc-fel-lipsa">{t("lc.catalog.ascunsa")}</span>}
        <MarcajScenariu difera={difera} lista="retete" cod={r.cod} />
        <span style={{ flex: 1 }} />
        {potScrie && r.pret_impus != null && (
          <button type="button" className="btn" title={r.sursa}
                  onClick={() => schimba({ pret_impus: undefined })}>{t("lc.sc.scoate_pret_impus")}</button>
        )}
        {potScrie && modificata && eGlobala && (
          <button type="button" className="btn" onClick={() => setCopii((c) => cuReteta(c, cod, cloneazaReteta(glob, cod)))}>{t("lc.catalog.revino")}</button>
        )}
        {potScrie && !eGlobala && (
          <button type="button" className="btn" onClick={() => setCopii((c) => cuReteta(c, cod, null))}>{t("lc.catalog.sterge")}</button>
        )}
      </div>
      <label className="lc-camp"><span>{t("lc.catalog.nume")}</span>
        <input value={r.nume} readOnly={ro} onChange={(e) => schimba({ nume: e.target.value })} /></label>
      <label className="lc-camp"><span>{t("lc.catalog.specificatii")}</span>
        <textarea rows={2} value={r.specificatii ?? ""} readOnly={ro} onChange={(e) => schimba({ specificatii: e.target.value })} /></label>
      <label className="lc-camp"><span>{t("lc.catalog.observatii")}</span>
        <textarea rows={2} value={r.observatii ?? ""} readOnly={ro} onChange={(e) => schimba({ observatii: e.target.value })} /></label>
      <div className="lc-camp-rand">
        <label className="lc-camp"><span>{t("struct.ret.th_um")}</span>
          <input value={r.um} readOnly={ro} style={{ width: 70 }} onChange={(e) => schimba({ um: e.target.value })} /></label>
        <label className="lc-camp"><span>{t("lc.catalog.cheie")}</span>
          <input value={r.cheie ?? ""} readOnly style={{ width: 220 }} title={t("lc.catalog.cheie_t")} /></label>
        {r.factori?.length ? r.factori.map((k) => (
          <label key={k} className="lc-camp"><span>{t(`lc.factor.${k}` as Cheie)}</span>
            <span className="lc-inline">
              <input type="number" min={0} step={1} value={copii.cantitati[k] ?? ""} readOnly={ro} style={{ width: 80 }}
                     placeholder={articol?.factori?.[k] != null ? nr(articol.factori[k], 2) : "—"}
                     onChange={(e) => setCopii((c) => cuCantitate(c, k, e.target.value === "" ? null : Number(e.target.value)))} />
              {copii.cantitati[k] == null && articol?.factori?.[k] != null && <span className="sg-muted">{t("lc.fel.estimat")}</span>}
            </span>
          </label>
        )) : (
        <label className="lc-camp"><span>{t("lc.catalog.cantitate_om")}</span>
          <span className="lc-inline">
            <input type="number" min={0} step="any" value={cantitateOm ?? ""} readOnly={ro} style={{ width: 110 }}
                   placeholder={articol ? nr(articol.cantitate, 2) : "—"}
                   onChange={(e) => setCopii((c) => cuCantitate(c, cod, e.target.value === "" ? null : Number(e.target.value)))} />
            <span className="sg-muted">{um(r.um)}</span>
            {articol && <span className="sg-muted" title={articol.sursa_cantitate.explicatie}>· {t("lc.catalog.masurat", { v: nr(articol.sursa_cantitate.calculata ?? articol.cantitate, 2) })}</span>}
          </span>
        </label>
        )}
        <label className="lc-camp lc-check"><input type="checkbox" checked={ascunsa} disabled={ro}
               onChange={(e) => setCopii((c) => cuAscunsa(c, cod, e.target.checked))} /> <span>{t("lc.catalog.ascunde")}</span></label>
      </div>

      <div className="lc-cutie">
        <table className="sg-table lc-resurse">
          <thead>
            <tr><th>{t("lc.res.tip")}</th><th>{t("lc.res.resursa")}</th><th>{t("lc.res.consum")}</th><th>{t("lc.res.pret")}</th><th>{t("lc.res.total")}</th><th /></tr>
          </thead>
          <tbody>
            {r.consumuri.map((c, i) => {
              const res = resursaProiectului(glob, copii, TIP_LA_LISTA[c.tip], c.cod);
              const altundeva = !!res && platitAltundeva(res, r);
              const pret = res && !altundeva ? (c.tip === "manopera" || c.tip === "utilaj" ? res.pret_ora ?? 0 : res.pret ?? 0) : 0;
              const u = c.tip === "manopera" || c.tip === "utilaj" ? t("lc.um.ora") : um(res?.um);
              return (
                <tr key={i} className={res ? undefined : "lc-lipsa"}>
                  <td className="sg-muted">{t(`lc.tip.${c.tip}`)}</td>
                  <td>{res?.nume ?? c.cod}
                    {altundeva && <div className="sg-muted">{t("lc.catalog.platit_in", { cod: res!.costul_in! })}</div>}</td>
                  <td className="sg-mono">
                    <input type="number" min={0} step="any" value={c.cantitate} readOnly={ro} style={{ width: 90 }}
                           onChange={(e) => consum(i, { cantitate: Number(e.target.value) })} /> {u}/{um(r.um)}
                  </td>
                  <td className="sg-mono">{pretUnitar(pret, u)}</td>
                  <td className="sg-mono">{bani(c.cantitate * pret, { zecimale: 2 })}</td>
                  <td>{potScrie && <button type="button" className="btn lc-x" title={t("lc.catalog.scoate")} onClick={() => consum(i, null)}>×</button>}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}><strong>{t("lc.catalog.pu")}</strong>
                <span className="sg-muted"> · {t("lc.catalog.pu_d", { m: pretUnitar(pu.material), l: pretUnitar(pu.manopera), u: pretUnitar(pu.utilaj), tr: pretUnitar(pu.transport) })}</span></td>
              <td className="sg-mono"><strong>{pretUnitar(pu.total, um(r.um))}</strong></td>
              <td />
            </tr>
          </tfoot>
        </table>
        {potScrie && (
          <div className="lc-adauga">
            <select value={tipNou} onChange={(e) => { setTipNou(e.target.value as TipResursa); setCodNou(""); }}>
              {TIPURI.map((x) => <option key={x} value={x}>{t(`lc.tip.${x}`)}</option>)}
            </select>
            <select value={codNou} onChange={(e) => setCodNou(e.target.value)}>
              <option value="">{t("lc.catalog.alege_resursa")}</option>
              {optiuni.map((o) => <option key={o.cod} value={o.cod}>{o.nume}</option>)}
            </select>
            <button type="button" className="btn" disabled={!codNou} onClick={adauga}>{t("lc.catalog.adauga_consum")}</button>
          </div>
        )}
        {r.sursa && <div className="sg-muted lc-sursa-pret">{t("lc.catalog.sursa", { s: r.sursa })}</div>}
        {r.pret_impus != null && (
          <div className="lc-sursa-pret"><span className="lc-fel lc-fel-sc">{t("lc.sc.pret_impus")}</span>{" "}
            {t("lc.sc.pret_impus_d", { p: pretUnitar(r.pret_impus, um(r.um)) })}</div>
        )}
      </div>
    </div>
  );
}

function Resurse({ glob, copii, potScrie, setCopii, difera = null, tinte = null, laPiata, laImport, listaCantitati = null }: {
  glob: CatalogGlobal; copii: CopiiProiect; potScrie: boolean; setCopii: (f: (c: CopiiProiect) => CopiiProiect) => void;
  difera?: DiferaDeProiect;
  /** lista proiectului: din ea se știe în ce faze consumă proiectul fiecare resursă */
  listaCantitati?: ListaCantitati | null;
  /** țintele pieței pe lista soluției: o resursă dintre ele are butonul «Piața» */
  tinte?: Tinta[] | null;
  laPiata?: (cheie: string) => void;
  laImport?: () => void;
}) {
  useT();
  const [lista, setLista] = useState<ListaResurse>("materiale");
  const [text, setText] = useState("");
  const [codNou, setCodNou] = useState("");
  const orar = lista === "manopera" || lista === "utilaje";
  const campPret = orar ? "pret_ora" : "pret";
  // numai resursele PROIECTULUI (clonate la calculul listei sau adăugate aici)
  const randuri = useMemo(() => {
    const q = text.trim().toLowerCase();
    return Object.keys(copii.resurse[lista] ?? {}).map((cod) => resursaProiectului(glob, copii, lista, cod)!)
      .filter((r) => !q || r.cod.toLowerCase().includes(q) || r.nume.toLowerCase().includes(q) || (r.categorie ?? "").toLowerCase().includes(q))
      .sort((a, b) => (a.categorie ?? "").localeCompare(b.categorie ?? "") || a.nume.localeCompare(b.nume));
  }, [glob, copii, lista, text]);
  const dinGlobal = Object.values(glob.resurse[lista] ?? {}).filter((r) => !copii.resurse[lista]?.[r.cod])
    .sort((a, b) => a.nume.localeCompare(b.nume));
  // FAZELE (Stefan, 31.10.2026): resursele stau pe faza proiectului în care le
  // consumă lista — etapele programului: structură subsol, suprastructură,
  // zidărie, finisaje… —, o resursă pe faza în care costă cel mai mult, cu
  // celelalte faze scrise sub nume. Fără listă (sau o listă veche, fără faze),
  // tabelul rămâne plat.
  const etape = listaCantitati?.etape ?? [];
  const faze = useMemo(() => fazeleResurselor(listaCantitati), [listaCantitati]);
  const fazeleLui = (cod: string) => faze.get(`${lista}:${cod}`) ?? [];
  const grupuri = etape.length
    ? grupatePeFaze(randuri, (r) => fazeleLui(r.cod)[0]?.etapa ?? null, etape)
    : [{ etapa: undefined as string | null | undefined, randuri }];
  return (
    <div>
      <div className="lc-filtre">
        <div className="bc-scen">
          {LISTE_RESURSE.map((l) => (
            <button key={l} type="button" aria-pressed={lista === l} onClick={() => { setLista(l); setCodNou(""); }}>
              {t(`lc.lista.${l}`)} <span className="sg-muted">{Object.keys(copii.resurse[l] ?? {}).length}</span></button>
          ))}
        </div>
        <input className="lc-cauta" type="search" value={text} placeholder={t("lc.cauta")} onChange={(e) => setText(e.target.value)} />
        {laImport && (
          <button type="button" className="btn" onClick={laImport}>{t("piata_res.import.buton")}</button>
        )}
        {potScrie && dinGlobal.length > 0 && (
          <span className="lc-inline">
            <select className="lc-select" value={codNou} onChange={(e) => setCodNou(e.target.value)}>
              <option value="">{t("lc.catalog.adauga_din_global", { n: dinGlobal.length })}</option>
              {dinGlobal.map((o) => <option key={o.cod} value={o.cod}>{o.nume}</option>)}
            </select>
            <button type="button" className="btn" disabled={!codNou} onClick={() => {
              setCopii((c) => cuResursa(c, lista, codNou, cloneazaResursa(glob, lista, codNou)));
              setCodNou("");
            }}>{t("comun.adauga")}</button>
          </span>
        )}
      </div>
      {randuri.length === 0 && <div className="sg-muted" style={{ padding: 8 }}>{t("lc.catalog.resurse_goale")}</div>}
      {randuri.length > 0 && (
        <div className="bc-scroll">
          <table className="sg-table lc-tabel">
            <thead>
              <tr><th>{t("lc.res.cod")}</th><th>{t("lc.res.resursa")}</th><th>{t("struct.ret.th_um")}</th>
                <th className="bc-n">{orar ? t("lc.res.pret_ora") : t("lc.res.pret")}</th><th>{t("lc.res.sursa")}</th><th /></tr>
            </thead>
            <tbody>
              {grupuri.map((gr) => [
                gr.etapa !== undefined && (
                  <tr key={`faza:${gr.etapa ?? "-"}`} className="lc-faza"><td colSpan={6}>{numeFaza(etape, gr.etapa)}</td></tr>
                ),
                ...gr.randuri.map((r) => {
                const v = (r as Record<string, unknown>)[campPret] as number | undefined;
                const alteFaze = fazeleLui(r.cod).slice(1).map((f) => numeFaza(etape, f.etapa));
                const g = glob.resurse[lista]?.[r.cod];
                const modificata = resursaDifera(glob, lista, copii.resurse[lista]?.[r.cod] ?? {}, r.cod);
                const dinPiata = !!copii.resurse[lista]?.[r.cod]?.piata;
                const cheiePiata = laPiata ? cheiaResursei(tinte, lista, r.cod) : null;
                return (
                  <tr key={r.cod} className={modificata ? "lc-rand-proiect" : undefined}>
                    <td className="sg-mono">{r.cod}</td>
                    <td>{r.nume}
                      {dinPiata ? <span className="lc-fel lc-fel-piata">{t("piata_res.chip")}</span>
                        : modificata && <span className="lc-fel lc-fel-om">{t("lc.catalog.modificata")}</span>}
                      <MarcajScenariu difera={difera} lista={lista} cod={r.cod} />
                      {r.nota && <div className="sg-muted">{r.nota}</div>}
                      {alteFaze.length > 0 && <div className="sg-muted">{t("lc.res.si_la", { faze: alteFaze.join(", ") })}</div>}</td>
                    <td className="sg-mono">{orar ? t("lc.um.ora") : um(r.um)}</td>
                    <td className="bc-n">
                      <input type="number" min={0} step="any" value={v ?? ""} readOnly={!potScrie} className="lc-pret"
                             onChange={(e) => setCopii((c) => cuResursa(c, lista, r.cod, cuPretDeOm(c.resurse[lista]?.[r.cod] ?? {}, campPret, Number(e.target.value))))} />
                      {modificata && g && <div className="sg-muted">{t("lc.catalog.global_era", { v: pretUnitar((g as Record<string, unknown>)[campPret] as number) })}</div>}
                    </td>
                    <td className="sg-muted lc-sursa">{r.sursa}</td>
                    <td><span className="lc-inline">{potScrie && modificata && g && (
                      <button type="button" className="btn lc-x" title={t("lc.catalog.revino")} onClick={() => setCopii((c) => cuResursa(c, lista, r.cod, cloneazaResursa(glob, lista, r.cod)))}>↺</button>
                    )}
                      {cheiePiata && laPiata && (
                        <button type="button" className="btn lc-x" title={t("piata_res.buton_t")}
                                onClick={() => laPiata(cheiePiata)}>{t("piata_res.buton")}</button>
                      )}</span></td>
                  </tr>
                );
                }),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CatalogBoq({ projectId, potScrie, lista, laLista, laSalvat, structura, scenariu = null,
                                    solutieId = null }: {
  projectId: string; potScrie: boolean; lista: ListaCantitati | null;
  /** soluția listei de cantități: fără ea, piața n-are ținte (nu se arată) */
  solutieId?: string | null;
  /** scenariul curent, ca rețetele și prețurile să se poată edita numai pe el */
  scenariu?: { id: string; nume: string; catalog: Suprascrieri | null | undefined;
               salveaza: (c: Suprascrieri) => Promise<void> } | null;
  /** înapoi la listă (butonul lipsește când pagina e fila de rețete) */
  laLista?: () => void;
  /** după o salvare: lista se reface pe catalogul nou */
  laSalvat?: () => void;
  /** rețetele de execuție ale structurii, cu desenul detaliului lor */
  structura?: { retete: RetetaStructura[]; detaliu: (id: string, inchide: () => void) => ReactNode };
}) {
  useT();
  const [glob, setGlob] = useState<CatalogGlobal | null>(null);
  const [copii, setCopiiRaw] = useState<CopiiProiect>(COPII_GOALE);
  const [salvate, setSalvate] = useState<CopiiProiect>(COPII_GOALE);
  const [rev, setRev] = useState(0);
  const [eroare, setEroare] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ecran, setEcran] = useState<"retete" | "resurse">("retete");
  const [ales, setAles] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [codNou, setCodNou] = useState("");
  const [subiecteAlese, setSubiecteAlese] = useState<Set<string>>(new Set());
  // toate subiectele desfăcute de la început: strânse, catalogul arăta nouă
  // rânduri și părea gol (Stefan, 28.10.2026)
  const [strinse, setStrinse] = useState<Set<string>>(new Set());
  // catalogul PROIECTULUI, așa cum e pe server; `copii` e ce se editează
  // (proiectul sau, pe scenariu, proiectul cu suprascrierile peste)
  const [baza, setBaza] = useState<CopiiProiect>(COPII_GOALE);
  // un scenariu care schimbă deja ceva se deschide pe el
  const [peScenariu, setPeScenariu] = useState(() => nrSuprascrieri(scenariu?.catalog) > 0);
  const peste = peScenariu && scenariu ? scenariu.catalog : null;
  const cheiePeste = JSON.stringify(peste ?? null);

  const incarca = useCallback(async () => {
    try {
      const c = await getCatalogBoq(projectId);
      setGlob(c.global);
      setBaza(c.proiect.data);
      setRev(c.proiect.rev);
      setEroare(null);
    } catch (e) {
      setEroare((e as Error).message);
    }
  }, [projectId]);
  useEffect(() => { void incarca(); }, [incarca]);
  // ce se vede: proiectul, cu scenariul peste când se editează scenariul
  useEffect(() => {
    const efectiv = suprapune(baza, peste);
    setCopiiRaw(efectiv);
    setSalvate(efectiv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baza, cheiePeste]);

  const setCopii = useCallback((f: (c: CopiiProiect) => CopiiProiect) => setCopiiRaw((c) => f(c)), []);
  const schimbat = JSON.stringify(copii) !== JSON.stringify(salvate);

  // PIAȚA: țintele și previzualizarea, citite numai când se văd resursele (o
  // cerere în plus pe deschiderea paginii n-ar folosi la nimic pe rețete)
  const [piata, setPiata] = useState<PiataProiect | null>(null);
  const [revPiata, setRevPiata] = useState(0);
  const [explorata, setExplorata] = useState<string | null>(null);
  const [importDeschis, setImportDeschis] = useState(false);
  useEffect(() => {
    if (ecran !== "resurse" || !solutieId) return;
    let viu = true;
    getPiataResurse(projectId, solutieId).then((d) => { if (viu) setPiata(d); }, () => { if (viu) setPiata(null); });
    return () => { viu = false; };
  }, [ecran, projectId, solutieId, revPiata]);
  // după un import: catalogul proiectului (prețurile și `rev`), lista și țintele
  const dupaPiata = useCallback(() => {
    void incarca();
    laSalvat?.();
    setRevPiata((n) => n + 1);
  }, [incarca, laSalvat]);

  async function salveaza() {
    setBusy(true);
    setEroare(null);
    try {
      if (peScenariu && scenariu) {
        await scenariu.salveaza(suprascrierile(baza, copii));
        setSalvate(copii);
      } else {
        const r = await putCatalogBoq(projectId, rev, copii);
        setRev(r.rev);
        setBaza(r.data);
      }
      laSalvat?.();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setEroare(t("lc.catalog.calcat"));
        await incarca();
      } else {
        setEroare((e as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (!glob) return eroare ? <div className="sg-error">{eroare}</div> : <SeIncarca />;

  const q = text.trim().toLowerCase();
  const potriveste = (...texte: (string | undefined)[]) => !q || texte.some((x) => (x ?? "").toLowerCase().includes(q));
  // NUMAI REȚETELE PROIECTULUI: clonele celor folosite de listă + cele adăugate
  const aleProiectului = Object.keys(copii.retete);
  const peSubiect = new Map<string, Map<string, Reteta[]>>();
  for (const cod of aleProiectului) {
    const r = retetaProiectului(glob, copii, cod)!;
    if (subiecteAlese.size && !subiecteAlese.has(r.subiect)) continue;
    if (!potriveste(r.cod, r.nume, r.specificatii)) continue;
    const s = peSubiect.get(r.subiect) ?? new Map<string, Reteta[]>();
    s.set(r.capitol, [...(s.get(r.capitol) ?? []), r]);
    peSubiect.set(r.subiect, s);
  }
  const aleStructurii = structura && (!subiecteAlese.size || subiecteAlese.has(STRUCTURA))
    ? structura.retete.filter((r) => potriveste(r.id, r.nume, r.categorie, ...r.activitati.map((a) => a.nume))) : [];
  const executiePeCapitol = new Map<string, RetetaStructura[]>();
  for (const r of aleStructurii) executiePeCapitol.set(capitolExecutie(r), [...(executiePeCapitol.get(capitolExecutie(r)) ?? []), r]);
  if (aleStructurii.length && !peSubiect.has(STRUCTURA)) peSubiect.set(STRUCTURA, new Map());
  // capitolele în ordinea catalogului, ca în listă
  const capitoleDe = (s: string) => {
    const ord = (glob.capitole[s] ?? []).map((c) => c.cod);
    const loc = (c: string) => (ord.includes(c) ? ord.indexOf(c) : ord.length);
    const chei = new Set([...peSubiect.get(s)!.keys(), ...(s === STRUCTURA ? executiePeCapitol.keys() : [])]);
    return [...chei].sort((a, b) => loc(a) - loc(b));
  };
  const ordine = glob.subiecte.map((s) => s.cod).filter((c) => peSubiect.has(c)).concat([...peSubiect.keys()].filter((c) => !glob.subiecte.some((s) => s.cod === c)));
  const nGasite = aleStructurii.length + [...peSubiect.values()].reduce((n, m) => n + [...m.values()].reduce((k, l) => k + l.length, 0), 0);
  const dinGlobal = glob.retete.filter((r) => !copii.retete[r.cod]).sort((a, b) => a.cod.localeCompare(b.cod));
  const comuta = (s: string) => setStrinse((d) => { const n = new Set(d); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  const desfacut = (s: string) => !strinse.has(s) || !!q;
  const comutaSubiect = (s: string) => setSubiecteAlese((a) => { const n = new Set(a); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  const nModif = aleProiectului.filter((cod) => retetaDifera(glob, copii.retete[cod] ?? {}, cod)).length
    + LISTE_RESURSE.reduce((n, l) => n + Object.keys(copii.resurse[l] ?? {}).filter((cod) => resursaDifera(glob, l, copii.resurse[l]![cod], cod)).length, 0)
    + Object.keys(copii.cantitati).length + copii.ascunse.length;
  const alesStructura = ales?.startsWith("str:") ? ales.slice(4) : null;
  const subiecteProiect = new Set(aleProiectului.map((cod) => retetaProiectului(glob, copii, cod)!.subiect));
  if (structura?.retete.length) subiecteProiect.add(STRUCTURA);
  const difera: DiferaDeProiect = peScenariu && scenariu ? (l, cod) => (l === "retete"
    ? JSON.stringify(copii.retete[cod] ?? null) !== JSON.stringify(baza.retete[cod] ?? null)
    : JSON.stringify(copii.resurse[l]?.[cod] ?? null) !== JSON.stringify(baza.resurse[l]?.[cod] ?? null)) : null;
  const nScenariu = nrSuprascrieri(peScenariu ? suprascrierile(baza, copii) : scenariu?.catalog);

  return (
    <div className="bc lc">
      <div className="bc-bara">
        {laLista && <button type="button" className="btn" onClick={laLista}>{t("lc.catalog.la_lista")}</button>}
        <span className="bc-lbl">{t("lc.catalog.titlu")}</span>
        <span className="sg-muted">{t("lc.catalog.versiune", { v: glob.versiune, n: nModif })}</span>
        <span style={{ flex: 1 }} />
        {scenariu && (
          <div className="bc-scen" role="group" aria-label={t("lc.sc.editezi")}>
            <button type="button" aria-pressed={!peScenariu} disabled={schimbat}
                    onClick={() => setPeScenariu(false)}>{t("lc.sc.proiectul")}</button>
            <button type="button" aria-pressed={peScenariu} disabled={schimbat} onClick={() => setPeScenariu(true)}>
              {t("lc.sc.scenariul", { nume: scenariu.nume })}
              {nScenariu > 0 && <span className="sg-muted"> {nScenariu}</span>}</button>
          </div>
        )}
        <div className="bc-scen">
          <button type="button" aria-pressed={ecran === "retete"} onClick={() => setEcran("retete")}>{t("lc.catalog.retete")}</button>
          <button type="button" aria-pressed={ecran === "resurse"} onClick={() => setEcran("resurse")}>{t("lc.catalog.resurse")}</button>
        </div>
        {potScrie && (
          <>
            <button type="button" className="btn" disabled={!schimbat || busy} onClick={() => setCopiiRaw(salvate)}>{t("comun.renunta")}</button>
            <button type="button" className="btn btnp" disabled={!schimbat || busy} onClick={() => void salveaza()}>
              {busy ? t("comun.se_salveaza") : t("comun.salveaza")}</button>
          </>
        )}
      </div>
      {eroare && <div className="sg-error">{eroare}</div>}
      <p className="bc-indiciu">{peScenariu && scenariu ? t("lc.sc.explicatie", { nume: scenariu.nume })
        : t("lc.catalog.explicatie")}</p>

      {ecran === "resurse" && (
        <Resurse glob={glob} copii={copii} potScrie={potScrie} setCopii={setCopii} difera={difera} listaCantitati={lista}
                 tinte={piata?.tinte ?? null}
                 laPiata={solutieId ? setExplorata : undefined}
                 laImport={solutieId && piata ? () => setImportDeschis(true) : undefined} />
      )}
      {explorata && solutieId && (
        <ExploreazaPiata projectId={projectId} solutieId={solutieId} cheie={explorata} potScrie={potScrie}
                         motivBlocat={schimbat ? t("piata_res.salveaza_intai") : null} colectatLa={piata?.colectat_la}
                         laInchide={() => setExplorata(null)} laSchimbat={dupaPiata} />
      )}
      {importDeschis && solutieId && (
        <ImportPiata projectId={projectId} solutieId={solutieId} potScrie={potScrie}
                     motivBlocat={schimbat ? t("piata_res.salveaza_intai") : null}
                     laInchide={() => setImportDeschis(false)} laSchimbat={dupaPiata} />
      )}
      {ecran === "retete" && (
        <>
          <div className="lc-filtre">
            <div className="lc-chips" role="group" aria-label={t("lc.subiecte")}>
              {glob.subiecte.filter((s) => subiecteProiect.has(s.cod)).map((s) => (
                <button key={s.cod} type="button" className={`lc-chip${subiecteAlese.has(s.cod) ? " lc-chip-ales" : ""}`}
                        aria-pressed={subiecteAlese.has(s.cod)} onClick={() => comutaSubiect(s.cod)}>{numeSubiect(glob, s.cod)}</button>
              ))}
              {subiecteAlese.size > 0 && (
                <button type="button" className="lc-chip lc-chip-sterge" onClick={() => setSubiecteAlese(new Set())}>{t("lc.toate")}</button>
              )}
            </div>
          </div>
          <div className="lc-filtre">
            <input className="lc-cauta" type="search" value={text} placeholder={t("lc.cauta")} onChange={(e) => setText(e.target.value)} />
            <span className="sg-muted">{t("lc.catalog.gasite", { n: nGasite })}</span>
            <span style={{ flex: 1 }} />
            {potScrie && dinGlobal.length > 0 && (
              <span className="lc-inline">
                <select className="lc-select" value={codNou} onChange={(e) => setCodNou(e.target.value)}>
                  <option value="">{t("lc.catalog.adauga_din_global", { n: dinGlobal.length })}</option>
                  {dinGlobal.map((r) => <option key={r.cod} value={r.cod}>{r.cod} · {r.nume}</option>)}
                </select>
                <button type="button" className="btn" disabled={!codNou} onClick={() => {
                  setCopii((c) => cuReteta(c, codNou, cloneazaReteta(glob, codNou)));
                  setAles(codNou);
                  setCodNou("");
                }}>{t("comun.adauga")}</button>
              </span>
            )}
            {potScrie && (
              <button type="button" className="btn" onClick={() => {
                const cod = `P.${Date.now().toString(36).toUpperCase()}`;
                const sub = [...subiecteAlese][0] ?? "arhitectura";
                setCopii((c) => cuReteta(c, cod, { cod, subiect: sub, capitol: glob.capitole[sub]?.[0]?.cod ?? "", nume: t("lc.catalog.reteta_noua"), um: "buc", cheie: null, consumuri: [] }));
                setAles(cod);
              }}>{t("lc.catalog.adauga_reteta")}</button>
            )}
          </div>
          <div className="lc-catalog">
            <div className="lc-lista">
              {ordine.map((s) => (
                <div key={s} className="lc-grup">
                  <button type="button" className="lc-grup-h" onClick={() => comuta(s)} aria-expanded={desfacut(s)}>
                    <span className="bc-sag">{desfacut(s) ? "▾" : "▸"}</span> {numeSubiect(glob, s)}
                    <span className="sg-muted"> {[...peSubiect.get(s)!.values()].reduce((n, l) => n + l.length, 0)
                      + (s === STRUCTURA ? aleStructurii.length : 0)}</span>
                  </button>
                  {desfacut(s) && capitoleDe(s).map((cap) => (
                    <div key={cap}>
                      <div className="lc-cap">{numeCapitol(glob, s, cap)}</div>
                      {s === STRUCTURA && (executiePeCapitol.get(cap) ?? []).map((r) => (
                        <button key={r.id} type="button" className={`lc-item${alesStructura === r.id ? " lc-item-ales" : ""}`}
                                title={t("lc.catalog.structura_nota")} onClick={() => setAles("str:" + r.id)}>
                          <span className="sg-mono lc-item-cod">{r.id.split("_")[0]}</span>
                          <span className="lc-item-nume">{r.nume}</span>
                          <span className="sg-mono lc-item-pu">{r.activitati.length} {t("lc.catalog.activitati")}</span>
                          <span className="lc-fel lc-fel-calculat">{t("lc.catalog.din_calcul")}</span>
                        </button>
                      ))}
                      {(peSubiect.get(s)!.get(cap) ?? []).map((r) => {
                        const pu = pretUnitarReteta(glob, copii, r);
                        const modificata = retetaDifera(glob, copii.retete[r.cod] ?? {}, r.cod);
                        return (
                          <button key={r.cod} type="button" className={`lc-item${ales === r.cod ? " lc-item-ales" : ""}`} onClick={() => setAles(r.cod)}>
                            <span className="sg-mono lc-item-cod">{r.cod}</span>
                            <span className="lc-item-nume">{r.nume}</span>
                            <span className="sg-mono lc-item-pu">{pretUnitar(pu.total, um(r.um))}</span>
                            {modificata && <span className="lc-fel lc-fel-om">{t("lc.catalog.modificata")}</span>}
                            <Rezerva r={r} />
                            {copii.ascunse.includes(r.cod) && <span className="lc-fel lc-fel-lipsa">{t("lc.catalog.ascunsa")}</span>}
                            {copii.cantitati[r.cod] != null && <span className="lc-fel lc-fel-om">{t("lc.fel.om")}</span>}
                            <MarcajScenariu difera={difera} lista="retete" cod={r.cod} />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
              {aleProiectului.length === 0 && <div className="sg-muted" style={{ padding: 8 }}>{t("lc.catalog.goala")}</div>}
              {aleProiectului.length > 0 && nGasite === 0 && <div className="sg-muted" style={{ padding: 8 }}>{t("lc.catalog.nimic")}</div>}
            </div>
            <div className="lc-editor-col">
              {alesStructura && structura ? structura.detaliu(alesStructura, () => setAles(null))
                : ales && copii.retete[ales] ? <EditorReteta glob={glob} copii={copii} cod={ales} potScrie={potScrie} setCopii={setCopii} lista={lista} difera={difera} />
                : <div className="sg-muted">{t("lc.catalog.alege")}</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
